import React from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'ai';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  /** Zeigt einen Spinner anstelle des Icons und sperrt den Knopf. */
  loading?: boolean;
}

// Der Farbverlauf laeuft nur ueber zwei benachbarte Stufen. Das liest sich als
// gewoelbte Flaeche, nicht als Verlauf — ein sichtbarer Verlauf wuerde in einer
// Maske mit zwanzig Knoepfen unruhig wirken.
const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-gradient-to-b from-blue-500 to-blue-600 text-white shadow-sm ' +
    'hover:from-blue-500 hover:to-blue-500 hover:shadow-md ' +
    'active:from-blue-600 active:to-blue-700 active:shadow-xs ' +
    'ring-1 ring-inset ring-white/20',
  secondary:
    'bg-white text-slate-700 border border-slate-200 shadow-xs ' +
    'hover:bg-slate-50 hover:border-slate-300 hover:shadow-sm ' +
    'active:bg-slate-100',
  danger:
    'bg-gradient-to-b from-red-500 to-red-600 text-white shadow-sm ' +
    'hover:from-red-500 hover:to-red-500 hover:shadow-md ' +
    'active:from-red-600 active:to-red-700 ' +
    'ring-1 ring-inset ring-white/20',
  ghost:
    'text-slate-600 hover:bg-slate-100 hover:text-slate-800 active:bg-slate-200',
  // Unterschied zu `secondary`: keine eigene Flaeche und kein Schatten. Der
  // Rahmen traegt die Tiefe allein — ein Tiefenmittel pro Element.
  outline:
    'bg-transparent text-slate-700 border border-slate-300 ' +
    'hover:bg-slate-50 hover:border-slate-400 ' +
    'active:bg-slate-100',
  ai:
    'bg-gradient-to-br from-violet-600 via-indigo-600 to-blue-600 text-white shadow-sm ' +
    'hover:shadow-md hover:brightness-110 active:brightness-95 ' +
    'ring-1 ring-inset ring-white/25',
};

const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-2.5 py-1.5 text-[11px] gap-1.5 rounded-lg',
  md: 'px-4 py-2 text-sm gap-2 rounded-lg',
  lg: 'px-5 py-2.5 text-sm gap-2 rounded-xl',
};

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  className = '',
  disabled,
  ...props
}) => {
  // Die Kurve ueberschwingt leicht: der Knopf federt zurueck statt zu rutschen.
  // 120ms, damit es sich schnell anfuehlt und nicht verspielt.
  const base =
    'inline-flex items-center justify-center font-medium select-none ' +
    'transition-[background-color,box-shadow,transform,filter,border-color] duration-150 ease-[cubic-bezier(0.34,1.56,0.64,1)] ' +
    'active:scale-[0.98] ' +
    'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ' +
    'disabled:opacity-55 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100';

  return (
    <button
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
      ) : icon ? (
        <span className="shrink-0">{icon}</span>
      ) : null}
      {children}
    </button>
  );
};
